import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const filterFieldDefaults = {
  universityType: "",
  isPartner: "all" as "all" | "true" | "false",
  isActive: "all" as "all" | "true" | "false",
  /** all | noCourses (0) | hasCourses (≥1) — sent as `hasCourses` query param */
  hasCourses: "all" as "all" | "noCourses" | "hasCourses",
  country: "",
  city: "",
};

export type UniversityFilterFieldDefaults = typeof filterFieldDefaults;

const initialState = {
  search: "",
  ...filterFieldDefaults,
  appliedFilters: {} as Record<string, any>,
};

export type UniversityFilterState = typeof initialState;

const universityFilterSlice = createSlice({
  name: "universityFilter",
  initialState,
  reducers: {
    setUniversitySearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
      state.appliedFilters = {
        ...state.appliedFilters,
        search: action.payload || undefined,
      };
      if (!action.payload) {
        delete (state.appliedFilters as any).search;
      }
    },
    setUniversityFilterField(
      state,
      action: PayloadAction<{
        key: keyof UniversityFilterFieldDefaults;
        value: any;
      }>
    ) {
      const { key, value } = action.payload;
      (state as any)[key] = value;
    },
    applyUniversityFilters(state) {
      const applied: Record<string, any> = {};
      const current = state as Record<string, any>;
      if (state.search) applied.search = state.search;
      for (const [key, defaultVal] of Object.entries(filterFieldDefaults)) {
        if (current[key] !== defaultVal && current[key] !== "") {
          applied[key] = current[key];
        }
      }
      state.appliedFilters = applied;
    },
    clearUniversityFilters() {
      return initialState;
    },
    /**
     * Programmatic preset (e.g. "Show only this country" called from elsewhere)
     * — overwrites both draft + applied so the list refilters immediately.
     */
    setUniversityAppliedFilters(
      state,
      action: PayloadAction<Partial<UniversityFilterFieldDefaults & { search: string }>>
    ) {
      const next = { ...filterFieldDefaults, search: "", ...action.payload };
      Object.assign(state, next);
      const applied: Record<string, any> = {};
      if (next.search) applied.search = next.search;
      for (const [key, defaultVal] of Object.entries(filterFieldDefaults)) {
        const val = (next as any)[key];
        if (val !== defaultVal && val !== "") applied[key] = val;
      }
      state.appliedFilters = applied;
    },
  },
});

export const {
  setUniversitySearch,
  setUniversityFilterField,
  applyUniversityFilters,
  clearUniversityFilters,
  setUniversityAppliedFilters,
} = universityFilterSlice.actions;

export default universityFilterSlice.reducer;
